import React from "react";
import classNames from "classnames";

import styles from "./DataSection.css";

function renderFraction(expression, numerator, denominator) {
  return (
    <div className={styles.equationContainer}>
      <div>{expression}</div>
      <div className={styles.equals}>=</div>
      <div className={styles.fractionContainer}>
        <div className={styles.numerator}>{numerator}</div>
        <div className={styles.denominator}>{denominator}</div>
      </div>
    </div>
  );
}

function DataSection() {
  return (
    <div className={styles.root}>
      {/* ---------- HELPFUL TERMS ---------- */}
      <section className={styles.sectionContainer}>
        <div id="terms" className={styles.sectionLineContainer}>
          <span className={classNames(styles.sectionLine, styles.red)} />
        </div>
        <h2 className={styles.sectionHeader}>Helpful Terms</h2>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Clinical categories and conditions</h3>
          <p>
            You'll notice we've categorized claims into broader clinical categories and more
            detailed clinical conditions. We've also supplied tooltips to give you descriptions and
            examples of what types of diagnoses are included within each. These categorizations,
            descriptions, and examples are informational only and intended to support data
            interpretation. This information should never be used to replace professional medical
            advice. Any clinical consultation or recommendation should be provided by a qualified
            healthcare professional.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Member months</h3>
          <p>
            The member months metric consists of the total number of enrolled members at a given
            point in time during a month. This metric is aggregated over time. When calculating
            metrics on average per enrolled member (e.g., claims spend, utilization) we use member
            months to account for fluctuations in population size.
          </p>
          <p>For a timeframe of January to March, member months would be:</p>
          <p>
            (count of enrolled members in January) + (count of enrolled members in February) +
            (count of enrolled members in March)
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Services classified as "Other"</h3>
          <p>
            You may notice some "other" and "miscellaneous" service categories display on occasion.
            These service categories are often used to group service codes that are not otherwise
            grouped under another subcategory of services.
          </p>
          <p>
            For inpatient services, "Other and "Medical" categories include medical treatments in an
            acute inpatient setting that are not surgeries, transplants, labor & delivery, newborn,
            or mental health related treatments. We often see "Other" include skilled nursing
            facility and hospice care services, and "Medical" include treatments for seizures,
            cancer, and infections.
          </p>
          <p>
            For outpatient services, "Other" includes a variety of service codes for durable medical
            equipment (DME) / prosthetics / supplies, home health, dialysis, and physical therapy
            facility charges. Professional "Other" includes service codes that are not otherwise
            grouped under subcategories of professional services.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Spend</h3>
          <p>
            The spend calculations represent a plan's financial liability based on processed claims
            - not necessarily paid on an invoice. Member liability is not included in these
            calculations.
          </p>
        </li>
      </section>

      {/* ---------- FILTERS ---------- */}
      <section className={styles.sectionContainer}>
        <div id="filters" className={styles.sectionLineContainer}>
          <span className={classNames(styles.sectionLine, styles.blue)} />
        </div>
        <h2 className={styles.sectionHeader}>Filters</h2>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Full plan year</h3>
          <p>
            This time frame represents the start of the plan year through the end of the plan year.
            For example, January 1, 2020 through December 31, 2020.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Minimum member population</h3>
          <p>
            Per Collective Health's privacy policy, we only report data with an average sample size
            of at least 11 eligible members. If the sample size is below the minimum member
            population, certain filter combinations won’t be available.
          </p>
          <p>
            This decision is based on the Centers for Medicare & Medicaid Services’ cell suppression
            policy.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Plan year-to-date</h3>
          <p>
            This time frame represents the start of the plan year through the current date. For
            example, January 1, 2020 through June 5, 2020.
          </p>
          <p>
            Please note: When plan year-to-date is selected, some metrics include a prorated partial
            month to estimate claims paid and/or member months when the current month isn’t yet
            complete. For the year-over-year comparisons of time frames with partial months, we
            prorate the prior year data as well for consistency.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Runout</h3>
          <p>
            You can use this filter to determine the length of lag time for a more complete view of
            claims spend.
          </p>
          <p>
            Though services may incur in one month, the claims for those services may not be
            processed until a few months later. To account for the delay between services and claims
            processing, this filter allows you to select an extended window of processing time to
            obtain a more complete view of claims from the incurred timeframe.
          </p>
          <p>
            When you select a runout filter, it selects claims data for services incurred within the
            time frame selected and processed through the end of the additional runout period
            selected: one month, three months, or all available. All available runout means the data
            will reflect claims processed as real time as possible, up through the last couple of
            days. For example, the “1 month” runout filter for claims data in January 2020 would
            show claims incurred in January 2020 and processed through the end of February 2020. For
            the “3 month” runout filter of the same example, you would see claims incurred in
            January 2020 and processed through the end of April 2020.
          </p>
          <p>
            The “processed” filter selects claims that were strictly processed during the selected
            time frame. It doesn't consider incurred dates and runout.
          </p>
        </li>
      </section>

      {/* ---------- DATA CALCULATIONS ---------- */}
      <section className={styles.sectionContainer}>
        <div id="calculations" className={styles.sectionLineContainer}>
          <span className={classNames(styles.sectionLine, styles.green)} />
        </div>
        <h2 className={styles.sectionHeader}>Data Calculations</h2>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Claimants/1000</h3>
          <p>
            Claimants/1000 is an extrapolation of the count of unique claimants that helps estimate
            how common a condition or service is among members. We use this metric instead of the
            count of unique claimants in order to preserve member privacy.
          </p>
          <p>
            This metric can be used to gauge year-over-year shifts in the number of claimants who
            have had a specific condition or service, despite fluctuations in the number of enrolled
            members. It is also annualized: for any chosen timeframe, we scale up or down to reflect
            a 12-month annual rate.
          </p>
          <p>
            We calculate claimants/1000 as: [the sum of unique claimants / sum of member months] x
            12 x 1,000
          </p>
          <p>
            The closer the claimants/1000 metric is to 1000 for a given condition or service, the
            greater proportion of the member population you can estimate to have had a claim for
            that same condition or service. For example, if we saw claimants/1000 equal to 500 for
            professional office visits, we could imply that roughly half of the member population
            had a claim for a professional office visit. Less common conditions or services - such
            as labor & delivery - will show as smaller values.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Network usage</h3>
          <p>
            We've highlighted how often your members are using your current network based on both
            dollars spent and volume of services, helping you quickly gauge whether there may be
            opportunities to reduce reliance on out-of-network providers (often tied to higher
            spend). These indicators are a good starting point to determine whether your members are
            sufficiently able to access in-network care with your current network partners.
          </p>
          <p>% Paid In-Network = [In-Network spend $ / Total spend $] x 100</p>
          <p>% In-Network Utilization = [In-Network claim count / Total claim count] x 100</p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>PMPM</h3>
          <p>
            This is an abbreviation of "per member, per month" spend. It’s the average spend per
            enrolled member on a monthly basis.
          </p>
          {renderFraction("PMPM calculation", "sum of claims paid amounts", "member months")}
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Unit cost</h3>
          <p>
            Unit cost represents the average amount the plan paid per encounter for a type of
            service.
          </p>
          {renderFraction(
            "Unit cost calculation",
            "sum of claims paid amounts",
            "sum of utilization count"
          )}
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Utilization/1000</h3>
          <p>
            Utilization/1000 measures how frequently members use healthcare services, where each
            claim represents a unique utilization count. This metric helps gauge how often members
            use different inpatient, outpatient, and professional services, and how this changes
            year-over-year, despite fluctuations in the number of enrolled members. Utilization/1000
            is also annualized: for any chosen timeframe, we scale up or down to reflect a 12-month
            annual rate. This helps us to better understand how increased or decreased use of
            specific services could affect cost and health outcomes.
          </p>
          <p>
            We calculate utilization/1000 as: [the sum of claim counts / sum of member months] x 12
            x 1,000
          </p>
          <p>
            Services that each member uses only once a year - think professional office visits for
            preventive care - will show utilization/1000 closer to 1000. For services that each
            member uses more frequently, utilization/1000 metrics will show as several thousands.
            For services used less frequently, utilization/1000 will show as a fraction of 1000.
          </p>
        </li>

        <li className={styles.termContainer}>
          <h3 className={styles.termHeader}>Year-over-year trend metrics</h3>
          <p>
            When displaying how much a metric has changed year-over-year, we always compare the
            current year metric to the prior year metric for the same range of incurred and
            processed dates. For example, if you've selected January 2020 + 1 month runout as the
            current timeframe, all year-over-year change metrics will compare to January 2019 + 1
            month runout.
          </p>
          <p>
            As you explore year-over-year changes, we recommend using a timeframe of at least six
            months with a minimum three month runout period. You’ll get a more reliable and
            informative comparison between plan years. With shorter timeframes and runout periods,
            you’ll often have more volatile and incomplete data which may cause a comparison to
            appear drastically lower or higher than reality.
          </p>
        </li>
      </section>
    </div>
  );
}

export default DataSection;
